import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from 'providers/AuthProvider';
import RyeLogo from 'components/Logo';
import { defaultLoginRedirectFlow, useQuery } from 'routes';
import { ReactComponent as GoogleIcon } from 'assets/icons/social/google.svg';
import config, { Routes } from 'config/constants';
import { trackSegmentEvent } from 'app/utils';
import { ACTION, SOURCE } from 'app/analytics';
import Separator from 'components/Separator';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Typography } from 'components/ui/typography';

import './style.scss';
import { useAppSelector } from 'app/store';
import { selectUserInfo } from 'app/store/user';
import { isLegacyBilling } from 'lib/utils';
import { env } from 'config/env';
import { shouldRequireEmailVerification } from 'app/emailVerification';
import { EmailVerificationGate } from 'components/EmailVerificationGate';
import { useFlags } from 'providers/FlagsProvider';

const LoginPage = () => {
  const navigate = useNavigate();
  const { requireEmailVerification } = useFlags();
  const allQueryParams = useQuery();
  const queryRedirect = allQueryParams.get('redirect');
  const queryEmail = allQueryParams.get('email');
  const [email, setEmail] = useState(queryEmail || '');
  const [password, setPassword] = useState('');
  const [, refreshEmailVerificationState] = useState(0);
  const { user, error, loading, signInOrRegisterWithGoogle, logInWithEmailAndPassword } = useAuth();
  const userInfo = useAppSelector(selectUserInfo);
  const { search } = useLocation();
  const registerLink = search ? `${Routes.Register}${search}` : Routes.Register;
  const requiresEmailVerification = shouldRequireEmailVerification(user, requireEmailVerification);

  const onLoginWithEmail = (e: React.SyntheticEvent) => {
    e.preventDefault();
    logInWithEmailAndPassword(email, password);
    trackSegmentEvent(SOURCE.LOGIN_FORM, ACTION.CLICK, 'login_with_email_button');
  };

  const onLoginWithGoogle = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    await signInOrRegisterWithGoogle();
    trackSegmentEvent(SOURCE.LOGIN_FORM, ACTION.CLICK, 'login_with_google_button');
  };

  useEffect(() => {
    if (!loading && requiresEmailVerification) {
      return;
    }

    const fallback = () => {
      if (user) {
        navigate(
          userInfo?.stripe?.paymentMethod ||
            isLegacyBilling(userInfo?.billingPeriodSettings) ||
            env.VITE_NODE_ENV === 'staging'
            ? config.routeDefaultHome
            : Routes.Payment,
        );
        return;
      }
    };

    defaultLoginRedirectFlow(
      navigate,
      loading,
      user,
      queryRedirect,
      fallback,
      allQueryParams ? `?${allQueryParams}` : '',
    );
  }, [
    user,
    userInfo?.stripe?.paymentMethod,
    loading,
    navigate,
    queryRedirect,
    allQueryParams,
    requiresEmailVerification,
  ]);

  useEffect(() => {
    if (error) {
      alert(error);
    }
  }, [error]);

  if (!loading && user && requiresEmailVerification) {
    return (
      <EmailVerificationGate
        user={user}
        onVerified={() => refreshEmailVerificationState((value) => value + 1)}
      />
    );
  }

  if (loading || (user && queryRedirect)) {
    return null;
  }

  return (
    <div className="sign-in">
      <div className="sign-in-container">
        <RyeLogo to={Routes.Login} />
        <Typography variant="small" as="div" className="sign-in-register-link">
          New to Rye?{' '}
          <Typography variant="link" asChild>
            <Link to={registerLink}>Sign up</Link>
          </Typography>
        </Typography>
        <Button variant="outline" className="sign-in-google" onClick={onLoginWithGoogle}>
          <GoogleIcon /> Sign in with Google
        </Button>

        <Separator />

        <form className="sign-in-form" onSubmit={onLoginWithEmail}>
          <Input
            className="sign-in-input"
            placeholder="E-mail Address"
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value.trim())}
            required
          />
          <Input
            className="sign-in-input"
            placeholder="Password"
            type="password"
            name="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <div className="sign-in-reset">
            <Typography variant="link" asChild>
              <Link to={Routes.Reset}>Forgot password?</Link>
            </Typography>
          </div>
          <Button type="submit" variant="secondary">
            Sign in with email
          </Button>
        </form>

        <Typography variant="small" as="p" className="sign-in-terms">
          By clicking “Continue with Google/Email” above, you acknowledge that you have read and
          understood, and agree to Rye's{' '}
          <Typography variant="link" asChild>
            <a href={config.external.terms} target="_blank" rel="noopener noreferrer">
              Terms and Conditions
            </a>
          </Typography>{' '}
          and{' '}
          <Typography variant="link" asChild>
            <a href={config.external.privacyPolicy} target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
          </Typography>
          .
        </Typography>
      </div>
    </div>
  );
};

export default LoginPage;
